import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../../app/useAolSaveHandler';
import { NoonReportSectionEnum } from '../../../../types/ViewModel/AolEnums';
import { AolDataItem } from '../../../../types/ViewModel/AolItem';
import { hasInvalidFields, hasMissingValues } from '../../../../utils/aolFieldsHelper';
import { ErrorText } from '../../../shared/error-text/ErrorText.style';
import OdfjellInputComponent, { InputChangeData, TimeInputChangeData } from '../../../common/odfjell-input-component/OdfjellInputComponent';
import { EnginesDataVM } from '../EnginesSection';
import { EngineListWrapper } from '../EnginesSection.style';
import { SectionInnerWrapper, SubSectionHeader } from '../../aol-container/AolContainer.style';
import { roundTo2Decimals } from '../../../../utils/numberHelper';

type Props = {
    enginesDataVM: EnginesDataVM;
    onChange: () => void;
    aolDataItem: AolDataItem;
    hoursInReport: number;
    hasEmptyValues: any;
    isReadOnly: boolean;
};

const ShorePowerSectionComponent: React.FC<Props> = ({ enginesDataVM, onChange, aolDataItem, hoursInReport, hasEmptyValues, isReadOnly }) => {
    const { saveAolEvent } = useAolSaveHandler();
    const [hasEmptyValuesLocal, setHasEmptyValuesLocal] = useState(false);

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.type === 'save_section' &&
            saveAolEvent.section === NoonReportSectionEnum.Engines &&
            aolDataItem &&
            saveAolEvent.aolDataItem.id === aolDataItem.id
        ) {
            checkOnSave();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        if (!!enginesDataVM && (enginesDataVM.shorePowerRunningHours == null || enginesDataVM.shorePowerRunningHours === 0)) {
            enginesDataVM.shorePowerRunningHours = hoursInReport;
        }
    }, [enginesDataVM]);

    useEffect(() => {
        setHasEmptyValuesLocal(false);
        hasEmptyValues.current = false;
    }, [aolDataItem]);

    const resetHasEmptyValues = () => {
        if (hasEmptyValuesLocal) {
            setHasEmptyValuesLocal(false);
            hasEmptyValues.current = false;
        }
    };

    const checkOnSave = () => {
        const ignoreList: string[] = [];
        const data = {
            incineratorConsumptionMgo: enginesDataVM.incineratorConsumptionMgo,
        };
        const hasMissing = hasMissingValues(data, ignoreList);

        if (hasMissing) {
            setHasEmptyValuesLocal(true);
            hasEmptyValues.current = true;
        }
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.type === 'checkbox' ? changeObject.event.target.checked : changeObject.event.target.value;

        if (changeObject.event.target.type === 'checkbox') {
            changeObject.dataObject[name] = value;

            if (value) {
                // set total report hours as default
                enginesDataVM.shorePowerRunningHours = hoursInReport;
            } else {
                // set shore power hours to 0 if checkbox for isShorePowerConnected is unselected
                enginesDataVM.shorePowerRunningHours = 0;
            }
        } else {
            changeObject.dataObject[name] = value ? +value : undefined;
        }

        resetHasEmptyValues();
        hasInvalidFields(enginesDataVM, name, changeObject.isValid);
        onChange();
    };

    const onTimeInputChange = (changeObject: TimeInputChangeData) => {
        changeObject.dataObject[changeObject.name] = changeObject.hours;
        resetHasEmptyValues();
        hasInvalidFields(enginesDataVM, changeObject.name, changeObject.isValid);
        onChange();
    };

    return (
        <>
            <SectionInnerWrapper>
                <SubSectionHeader>Shore Power</SubSectionHeader>
                <EngineListWrapper>
                    <OdfjellInputComponent
                        isReadOnly={isReadOnly}
                        type={'checkbox'}
                        description={'Connected to shore power'}
                        dataObject={enginesDataVM}
                        name={'isShorePowerConnected'}
                        value={enginesDataVM.isShorePowerConnected}
                        onChange={onInputChange}
                    />
                    {enginesDataVM.isShorePowerConnected && (
                        <>
                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                description={'Hours connected'}
                                type={'time'}
                                value={enginesDataVM.shorePowerRunningHours}
                                onTimeInputChange={onTimeInputChange}
                                name={'shorePowerRunningHours'}
                                dataObject={enginesDataVM}
                                max={roundTo2Decimals(aolDataItem.reportHours)}
                            ></OdfjellInputComponent>
                            <OdfjellInputComponent
                                isReadOnly={isReadOnly}
                                description={'kWh received'}
                                type={'number'}
                                value={enginesDataVM.shorePowerKwhReceived}
                                onChange={onInputChange}
                                name={'shorePowerKwhReceived'}
                                dataObject={enginesDataVM}
                                maxDecimals={2}
                                unit={'kWh'}
                            ></OdfjellInputComponent>
                        </>
                    )}
                </EngineListWrapper>
            </SectionInnerWrapper>
            {hasEmptyValuesLocal && <ErrorText>You must enter shore power hours before saving.</ErrorText>}
        </>
    );
};

export default ShorePowerSectionComponent;
